import { useAppSelector } from '@/app/hooks'
import { TrendingUp, TrendingDown, Wallet, Users, MapPin, Package, Minus } from 'lucide-react'
import {
  useDashboardOverviewQuery,
  useTopProductsQuery,
  useTopCustomersQuery,
} from '@/features/reports/reports-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/misc'
import { ErrorState } from '@/components/shared/list-states'
import type { PeriodAggregate, TrendDay } from '@/features/reports/reports-types'
import { formatPaisa } from '@/lib/format'

function pctChange(current: number, prev: number): number | null {
  if (prev === 0) return current === 0 ? 0 : null
  return ((current - prev) / Math.abs(prev)) * 100
}

function Delta({ current, prev }: { current: number; prev?: number }) {
  if (prev === undefined) return null
  const change = pctChange(current, prev)
  if (change === null) return <span className="text-[10px] text-muted-foreground">new</span>

  const rounded = Math.round(change)
  const flat = rounded === 0
  const up = rounded > 0
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown
  const tone = flat ? 'text-muted-foreground' : up ? 'text-emerald-600' : 'text-red-600'

  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${tone}`}>
      <Icon className="h-3 w-3" />
      {flat ? '0%' : `${up ? '+' : ''}${rounded}%`}
    </span>
  )
}

export function DashboardPage() {
  const user = useAppSelector((s) => s.auth.user)
  const company = useAppSelector((s) => s.auth.company)

  const { data, isLoading, isError, refetch } = useDashboardOverviewQuery()
  const overview = data?.data

  const now = new Date()
  const monthParams = { year: now.getFullYear(), month: now.getMonth() + 1, limit: 5 }
  const { data: productsData } = useTopProductsQuery(monthParams)
  const { data: customersData } = useTopCustomersQuery(monthParams)
  const topProducts = productsData?.data ?? []
  const topCustomers = customersData?.data ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold">
          Welcome back{user ? `, ${user.full_name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-muted-foreground">
          {company?.name ?? 'Loading…'}
          {overview && (
            <span className="ml-2 text-xs">
              · As of {new Date(overview.as_of).toLocaleString()}
            </span>
          )}
        </p>
      </div>

      {isError ? (
        <Card><ErrorState onRetry={refetch} /></Card>
      ) : isLoading || !overview ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-64 w-full" />
        </>
      ) : (
        <>
          <div>
            <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              Today
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                label="Revenue"
                value={formatPaisa(overview.today.revenue)}
                icon={<TrendingUp className="h-4 w-4" />}
                accent="brand"
              />
              <KpiCard
                label="Orders confirmed"
                value={overview.today.orders_confirmed_count.toString()}
                sub={`AOV ${formatPaisa(overview.today.avg_order_value)}`}
              />
              <KpiCard
                label="Payments"
                value={formatPaisa(overview.today.payments_total)}
                sub={`${overview.today.payments_count} txns`}
                icon={<Wallet className="h-4 w-4" />}
              />
              <KpiCard
                label="Visits"
                value={overview.today.visits_count.toString()}
                sub={
                  overview.today.visits_count > 0
                    ? `${overview.today.visits_strike_rate_pct.toFixed(0)}% productive`
                    : 'No visits yet'
                }
                icon={<MapPin className="h-4 w-4" />}
              />
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <PeriodCard label="This week" period={overview.this_week} prev={overview.prev_week} prevLabel="last week" />
            <PeriodCard label="This month" period={overview.this_month} prev={overview.prev_month} prevLabel="last month" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Revenue trend — last 30 days</CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart data={overview.last_30_days_trend} />
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="h-4 w-4 text-muted-foreground" /> Top products this month
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topProducts.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">No product sales yet this month.</p>
                ) : (
                  <ul className="space-y-3">
                    {topProducts.map((p, i) => (
                      <li key={p.product_id ?? p.sku} className="flex items-center gap-3">
                        <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{p.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {p.units_sold} units · {p.margin_pct.toFixed(0)}% margin
                          </p>
                        </div>
                        <p className="font-display text-sm font-semibold">{formatPaisa(p.revenue_total)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-muted-foreground" /> Top customers this month
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topCustomers.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">No customer orders yet this month.</p>
                ) : (
                  <ul className="space-y-3">
                    {topCustomers.map((c, i) => (
                      <li key={c.customer_id} className="flex items-center gap-3">
                        <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{c.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {c.orders_count} orders
                            {c.closing_balance > 0 ? ` · ${formatPaisa(c.closing_balance)} due` : ''}
                          </p>
                        </div>
                        <p className="font-display text-sm font-semibold">{formatPaisa(c.orders_total)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}

function KpiCard({
  label,
  value,
  sub,
  icon,
  accent,
}: {
  label: string
  value: string
  sub?: string
  icon?: React.ReactNode
  accent?: 'brand'
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
          {icon && <span className="text-muted-foreground">{icon}</span>}
        </div>
        <p className={`mt-1.5 font-display text-2xl font-semibold ${accent === 'brand' ? 'text-accent' : ''}`}>
          {value}
        </p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  )
}

function PeriodCard({
  label,
  period,
  prev,
  prevLabel,
}: {
  label: string
  period: PeriodAggregate
  prev?: PeriodAggregate
  prevLabel?: string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{label}</CardTitle>
        <p className="text-xs text-muted-foreground">
          {period.from} → {period.to}
          {prev && prevLabel ? <span className="ml-1">· vs {prevLabel}</span> : null}
        </p>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4">
        <Stat label="Revenue" value={formatPaisa(period.revenue)} delta={<Delta current={period.revenue} prev={prev?.revenue} />} />
        <Stat label="Orders" value={`${period.orders_confirmed_count} / ${period.orders_count}`} sub="confirmed / total" delta={<Delta current={period.orders_confirmed_count} prev={prev?.orders_confirmed_count} />} />
        <Stat label="Payments" value={formatPaisa(period.payments_total)} sub={`${period.payments_count} txns`} delta={<Delta current={period.payments_total} prev={prev?.payments_total} />} />
        <Stat label="AOV" value={formatPaisa(period.avg_order_value)} delta={<Delta current={period.avg_order_value} prev={prev?.avg_order_value} />} />
        <Stat label="New customers" value={period.customers_new.toString()} icon={<Users className="h-3 w-3" />} delta={<Delta current={period.customers_new} prev={prev?.customers_new} />} />
        <Stat
          label="Visits"
          value={period.visits_count.toString()}
          sub={period.visits_count > 0 ? `${period.visits_strike_rate_pct.toFixed(0)}% productive` : undefined}
          delta={<Delta current={period.visits_count} prev={prev?.visits_count} />}
        />
        <Stat label="Active reps" value={period.reps_active_count.toString()} />
        <Stat
          label="Returns"
          value={formatPaisa(period.returns_total)}
          sub={`${period.returns_count} cases`}
          delta={<Delta current={period.returns_total} prev={prev?.returns_total} />}
        />
      </CardContent>
    </Card>
  )
}

function Stat({
  label,
  value,
  sub,
  icon,
  delta,
}: {
  label: string
  value: string
  sub?: string
  icon?: React.ReactNode
  delta?: React.ReactNode
}) {
  return (
    <div>
      <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </p>
      <div className="mt-0.5 flex items-center gap-2">
        <p className="text-sm font-medium">{value}</p>
        {delta}
      </div>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  )
}

function TrendChart({ data }: { data: TrendDay[] }) {
  if (data.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No trend data yet.</p>
  }

  const maxRev = Math.max(...data.map((d) => d.revenue), 1)
  const width = 600
  const height = 140
  const padding = { top: 8, right: 8, bottom: 20, left: 8 }
  const w = width - padding.left - padding.right
  const h = height - padding.top - padding.bottom

  const xStep = data.length > 1 ? w / (data.length - 1) : 0
  const points = data.map((d, i) => {
    const x = padding.left + i * xStep
    const y = padding.top + h - (d.revenue / maxRev) * h
    return { x, y, d }
  })

  const path = points
    .map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`))
    .join(' ')

  const lastPoint = points[points.length - 1]
  const firstPoint = points[0]
  const areaPath =
    lastPoint && firstPoint
      ? `${path} L${lastPoint.x},${padding.top + h} L${firstPoint.x},${padding.top + h} Z`
      : path

  const totalRev = data.reduce((s, d) => s + d.revenue, 0)

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            30-day total
          </p>
          <p className="font-display text-2xl font-semibold">{formatPaisa(totalRev)}</p>
        </div>
        <div className="text-right">
          <Badge variant="muted">
            Peak: {formatPaisa(maxRev)}
          </Badge>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-32 w-full" preserveAspectRatio="none">
        <path d={areaPath} fill="currentColor" className="text-accent/10" />
        <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-accent" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={1.5} fill="currentColor" className="text-accent" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  )
}
